import Throttler from "@/services/throttler";
import {
  useTenantsStore,
  useSitesStore,
  useUsersStore,
  // useAuthStore,
  useSessionStore,
  usePasskeysStore,
  useItemsStore,
  useTradeItemsStore,
  useItemBlocksStore,
  useReplacementItemsStore,
  useStockLevelsStore,
  useSuppliersStore,
  useSupplierItemsStore,
  useItemSiteSettingsStore,
  useForeignItemsStore,
  useLabelTemplatesStore,
  useLabelPrintersStore,
  useUserSettingsStore,
  useUserLinkedServicesStore,
  // useStockStore,
  // useStockChangeStore,
  // useDeliveriesStore,
  // useOrdersStore,
  // useStockDemandStore,
} from "@/stores";
import { ref } from "vue";

const organizationLoadThrottler = new Throttler();
const identityLoadThrottler = new Throttler();
const masterdataLoadThrottler = new Throttler();

export const isLoadingOrganization = ref(false);
export const isLoadingIdentity = ref(false);
export const isLoadingMasterdata = ref(false);
export const isLoadingSiteMasterdata = ref(false);

let currentTenantId = 0;
let currentSiteId = 0;

export const loadOrganization = async (force = false) => {
  isLoadingOrganization.value = true;

  try {
    // 1. load tenants
    await useTenantsStore().fetchAll(force);

    // 2. load sites
    await useSitesStore().fetchAll(force);

    // 3. load other organization data
    await Promise.all([
      useUsersStore().fetchAll(force),
      useSessionStore().fetchAll(force),
      usePasskeysStore().fetchAll(force),
    ]);
  }
  finally {
    isLoadingOrganization.value = false;
  }
};

export const loadIdentity = async (force = false) => {
  isLoadingIdentity.value = true;
  
  try {
    await Promise.all([
      useUsersStore().fetchAll(force),
      useSessionStore().fetchAll(force),
      useUserSettingsStore().fetchAll(force),
    ]);
  }
  finally {
    isLoadingIdentity.value = false;
  }
};

export const loadTenantMasterdata = async (force = false) => {
  const sessionStore = useSessionStore();
  if (sessionStore.selectedTenantId === 0 || sessionStore.selectedSiteId === 0) return;
  if (!force && sessionStore.selectedTenantId === currentTenantId && sessionStore.selectedSiteId === currentSiteId) return;

  isLoadingMasterdata.value = true;
  try {
    currentTenantId = sessionStore.selectedTenantId;
    currentSiteId = sessionStore.selectedSiteId;
    
    // Phase 1
    console.log("tenant master data loading (phase 1)");
    await Promise.all([
      useItemsStore().fetchAll(force).then(() => {
        Promise.all([
          useItemSiteSettingsStore().fetchAll(force),
          useTradeItemsStore().fetchAll(force),
          useItemBlocksStore().fetchAll(force),
          useReplacementItemsStore().fetchAll(force),
          useForeignItemsStore().fetchAll(force)
        ]); // ensure these are loaded after items
      }),
      useLabelTemplatesStore().fetchAll(force),
      useLabelPrintersStore().fetchAll(force),
      useStockLevelsStore().fetchAll(force),
      useSuppliersStore().fetchAll(force),
      useUserLinkedServicesStore().fetchAll(force),
    ]);

    // Phase 2
    console.log("tenant master data loading (phase 2)");
    await Promise.all([
      useSupplierItemsStore().fetchAll(force), // ansure loaded after suppliers and items
    ]);
  }
  finally {
    isLoadingMasterdata.value = false;
  }
};

export const loadSiteMasterdata = async (force = false) => {
  const sessionStore = useSessionStore();
  if (sessionStore.selectedTenantId === 0 || sessionStore.selectedSiteId === 0) return;
  if (!force && sessionStore.selectedTenantId === currentTenantId && sessionStore.selectedSiteId === currentSiteId) return;

  isLoadingSiteMasterdata.value = true;
  try {
    currentTenantId = sessionStore.selectedTenantId;
    currentSiteId = sessionStore.selectedSiteId;

    // Phase 1
    console.log("site master data loading (phase 1)");
    await Promise.all([
      useItemSiteSettingsStore().fetchAll(force),
      useStockLevelsStore().fetchAll(force),
      useItemBlocksStore().fetchAll(force),
      useForeignItemsStore().fetchAll(force),
      useLabelPrintersStore().fetchAll(force),
    ]);
  }
  finally {
    isLoadingSiteMasterdata.value = false;
  }
};

export const throttledLoadOrganization = organizationLoadThrottler.throttle(
  async () => {
    console.log("Load Organization");
    await loadOrganization(true);
  },
  [],
  3000,
  { leading: true, trailing: true }
);

export const throttledLoadIdentiy = identityLoadThrottler.throttle(
  async () => {
    console.log("Load Identity");
    await loadIdentity(true);
  },
  [],
  3000,
  { leading: true, trailing: true }
);

export const throttledLoadTenantMasterdata = masterdataLoadThrottler.throttle(
  async () => {
    console.log("Load Tenant Masterdata");
    await loadTenantMasterdata(true);
  },
  [],
  3000,
  { leading: true, trailing: true }
);

export const throttledLoadSiteMasterdata = masterdataLoadThrottler.throttle(
  async () => {
    console.log("Load Site Masterdata");
    await loadSiteMasterdata(true);
  },
  [],
  3000,
  { leading: true, trailing: true }
);
